import React from 'react';
import { Shield, ArrowLeft, Building2, Scale, Users, History } from 'lucide-react';

interface CommunityLicenseProps {
  onBack: () => void;
}

export const CommunityLicense: React.FC<CommunityLicenseProps> = ({ onBack }) => {
  const sections = [
    {
      icon: Users,
      khmer: "១. សិទ្ធិប្រើប្រាស់ និងការគាំទ្រ (Usage & Support)",
      content: "កម្មវិធីនេះអនុញ្ញាតឱ្យប្រើប្រាស់ដោយ ឥតគិតថ្លៃ (Free to Use)។ ដើម្បីផ្គត់ផ្គង់ការចំណាយលើប្រតិបត្តិការ និងការអភិវឌ្ឍ ក្រុមហ៊ុន E-KHMER Technology Co., Ltd. ស្វាគមន៍រាល់ការបរិច្ចាគ (Donations) និងរក្សាសិទ្ធិក្នុងការរកប្រាក់ចំណូលតាមរយៈការផ្សព្វផ្សាយពាណិជ្ជកម្ម (Ads) ឬសេវាកម្មផ្សេងៗ។",
      english: "(Users may use this platform for free. To cover development and operating costs, E-KHMER Technology Co., Ltd. welcomes donations and reserves the right to monetize via advertisements or other services.)"
    },
    {
      icon: Building2,
      khmer: "២. កម្មសិទ្ធិបច្ចុប្បន្ន (Current Ownership)",
      content: "បច្ចុប្បន្ននេះ កម្មវិធីនិងកូដ (Source Code) ត្រូវបានអភិវឌ្ឍនិងថែរក្សាដោយក្រុមហ៊ុន E-KHMER Technology Co., Ltd.។ ដើម្បីធានាសុវត្ថិភាពមុនពេលលក់ Token កូដត្រូវបានរក្សាទុកជាឯកជន (Closed Source)។ ហាមដាច់ខាតការចម្លង ឬបំបែកកូដ (Reverse Engineering) ដោយគ្មានការអនុញ្ញាត។",
      english: "(Currently, the source code is proprietary and maintained by E-KHMER Technology Co., Ltd. for security purposes. Reverse engineering or unauthorized copying is strictly prohibited.)"
    },
    {
      icon: History,
      khmer: "៣. ការសន្យាអនាគត & Token Sale (Future Transition)",
      content: "នេះជាកិច្ចសន្យារបស់យើង៖ នៅពេលការលក់ Token (Token Sale) បានបញ្ចប់ជាស្ថាពរ ក្រុមហ៊ុននឹងដាក់ឱ្យប្រើប្រាស់កូដជាសាធារណៈ (Open Source) ក្រោមអាជ្ញាប័ណ្ណ Apache License 2.0។ នៅពេលនោះ ការសម្រេចចិត្តនឹងត្រូវធ្វើឡើងតាមរយៈសហគមន៍ (Community Decision/DAO)។",
      english: "(We pledge to release the source code under the Apache License 2.0 upon completion of the Token Sale. Governance rights will then transfer to the Community.)"
    },
    {
      icon: Scale,
      khmer: "៤. ការបដិសេធការទទួលខុសត្រូវ (Limitation of Liability)",
      content: "កម្មវិធីនេះត្រូវបានផ្តល់ជូន \"ដូចដែលបានមាន\" (AS IS) ដោយគ្មានការធានាណាមួយឡើយ។ ក្រុមហ៊ុន E-KHMER Technology Co., Ltd. មិនទទួលខុសត្រូវចំពោះការបាត់បង់ទិន្នន័យ ឬទ្រព្យសម្បត្តិណាមួយដែលកើតឡើងពីការប្រើប្រាស់កម្មវិធីនេះឡើយ។ អ្នកប្រើប្រាស់ត្រូវទទួលខុសត្រូវដោយខ្លួនឯង។",
      english: "(The software is provided \"AS IS\" without warranty of any kind. E-KHMER Technology Co., Ltd. is not liable for any damages or losses arising from the use of this platform. Users assume full responsibility.)"
    }
  ];

  return (
    <div className="min-h-screen bg-slate-900 relative overflow-hidden py-20 px-6">
      {/* Background blobs */}
      <div className="absolute top-[-10%] left-[-10%] w-[50%] h-[50%] rounded-full bg-violet-600/20 blur-[120px]"></div>
      <div className="absolute bottom-[-10%] right-[-10%] w-[50%] h-[50%] rounded-full bg-fuchsia-600/20 blur-[120px]"></div>

      <div className="max-w-4xl mx-auto relative z-10">
        <button 
          onClick={onBack}
          className="mb-8 flex items-center gap-3 text-white/60 hover:text-white transition-colors group font-black uppercase text-[10px] tracking-widest"
        >
          <div className="w-10 h-10 rounded-xl bg-white/5 flex items-center justify-center border border-white/10 group-hover:border-white/20">
            <ArrowLeft size={18} />
          </div>
          Back to Login
        </button>

        <div className="bg-white/5 backdrop-blur-3xl rounded-[3.5rem] border border-white/10 p-8 md:p-16 shadow-2xl">
          <div className="flex flex-col items-center text-center mb-16">
            <div className="w-20 h-20 bg-gradient-to-tr from-violet-600 to-fuchsia-600 rounded-[2rem] flex items-center justify-center shadow-2xl shadow-violet-500/40 mb-8">
              <Shield className="text-white" size={40} />
            </div>
            <h1 className="text-4xl md:text-5xl font-black text-white tracking-tighter mb-4">
               អាជ្ញាប័ណ្ណសហគមន៍
            </h1>
            <p className="text-transparent bg-clip-text bg-gradient-to-r from-violet-400 to-fuchsia-400 font-black text-sm uppercase tracking-[0.3em]">
              Community License
            </p>
            <div className="mt-6 inline-flex items-center gap-2 px-4 py-2 bg-white/5 rounded-full border border-white/10">
               <div className="w-2 h-2 rounded-full bg-emerald-400 animate-pulse"></div>
               <span className="text-[10px] font-black text-white/60 uppercase tracking-widest">Phase: Development & Pre-Token</span>
            </div>
          </div>

          <div className="grid grid-cols-1 gap-12">
            {sections.map((section, idx) => (
              <div key={idx} className="relative group">
                <div className="absolute -left-4 md:-left-8 top-0 bottom-0 w-1 bg-gradient-to-b from-violet-500/50 to-transparent rounded-full opacity-0 group-hover:opacity-100 transition-opacity"></div>
                <div className="flex items-start gap-6">
                  <div className="w-12 h-12 rounded-2xl bg-white/5 border border-white/10 flex items-center justify-center text-violet-400 shrink-0 shadow-lg group-hover:bg-violet-600 group-hover:text-white group-hover:border-violet-500 transition-all duration-300">
                    <section.icon size={24} />
                  </div>
                  <div className="space-y-4">
                    <h3 className="text-xl font-black text-white tracking-tight">{section.khmer}</h3>
                    <p className="text-slate-300 text-lg leading-relaxed font-medium">
                      {section.content}
                    </p>
                    <p className="text-slate-500 text-sm font-bold italic leading-relaxed">
                      {section.english}
                    </p>
                  </div>
                </div>
              </div>
            ))}
          </div>

          <div className="mt-20 pt-10 border-t border-white/5 text-center">
            <p className="text-[10px] font-black text-slate-500 uppercase tracking-[0.5em] mb-4">Developed By</p>
            <h4 className="text-white font-black text-lg tracking-tight uppercase">E-KHMER Technology Co., Ltd.</h4>
            <p className="text-slate-500 text-xs mt-2">Phnom Penh, Cambodia</p>
          </div>
        </div>
      </div>
    </div>
  );
};